/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchPropertiesByOwner, fetchPropertyGroupsByOwner } from '../../misc/apiRequests';
import PropertyDisplayThumb from './propertyDisplayThumb';
import PageIndex from '../pageIndex';
import Swal from 'sweetalert2';

const PropertyByOwner = ({
  user,
  handlePropertyRequests, handlePropertyRemove, handlePropertySelect, handleLoader, handleModal,
  setRedirect
}) => {
  const { t } = useTranslation();
  let { id } = useParams();

  const [properties, setProperties] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [maxPages, setMaxPages] = useState(1);

  const [groups, setGroups] = useState([]);
  const [groupPerPage, setGroupPerPage] = useState(10);
  const [groupPage, setGroupPage] = useState(1);
  const [groupMaxPages, setGroupMaxPages] = useState(1);

  const [ownerName, setOwnerName] = useState('');

  const errorToast = (errors) => {
    Swal.fire({
      position: 'top-end',
      toast: true,
      timerProgressBar: true,
      icon: 'error',
      title: Array.isArray(errors) ? errors.join("\n") : errors,
      showConfirmButton: false,
      timer: 2000
    })
  };

  // The endpoints don't return the owner separately, so pull the name off
  // whichever record carries it and fall back to a generic heading.
  const nameFromRecord = (record) => {
    const profile = record?.owner?.profile || record?.user?.profile;
    if (!profile) return '';
    return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  };

  // Fetch the owner's individual listings
  useEffect(() => {
    if (!id) return;

    handleLoader(true);
    fetchPropertiesByOwner(id, perPage, page)
      .then(response => {
        if (response.success) {
          setProperties(response.records || []);
          setMaxPages(response.records_params?.max_page || 1);
          setOwnerName(current => current || nameFromRecord(response.records?.[0]));
        }
        if (!response.success) errorToast(response.errors);
        handleLoader(false);
      });
  }, [handleLoader, id, page, perPage])

  // Fetch the owner's complexes
  useEffect(() => {
    if (!id) return;

    handleLoader(true);
    fetchPropertyGroupsByOwner(id, groupPerPage, groupPage)
      .then(response => {
        if (response.success) {
          setGroups(response.records || []);
          setGroupMaxPages(response.records_params?.max_page || 1);
          setOwnerName(current => current || nameFromRecord(response.records?.[0]));
        }
        if (!response.success) errorToast(response.errors);
        handleLoader(false);
      });
  }, [handleLoader, id, groupPage, groupPerPage])

  const populateGroups = () => groups.map(groupData => (
    <PropertyDisplayThumb
      key={`group-${groupData.id}`}
      property={groupData}
      user={user}
      handlePropertyRequests={handlePropertyRequests}
      handlePropertyRemove={handlePropertyRemove}
      handlePropertySelect={handlePropertySelect}
      setRedirect={setRedirect}
      showPropertyType={false}
      showIsland={true}
      isPropertyGroup={true}
    />
  ));

  const populateProperties = () => properties.map(propertyData => (
    <PropertyDisplayThumb
      key={`property-${propertyData.id}`}
      property={propertyData}
      user={user}
      handlePropertyRequests={handlePropertyRequests}
      handlePropertyRemove={handlePropertyRemove}
      handlePropertySelect={handlePropertySelect}
      setRedirect={setRedirect}
      showPropertyType={true}
      showIsland={true}
      isPropertyGroup={false}
    />
  ));

  const nothingListed = groups.length === 0 && properties.length === 0;

  return (
    <div id='PropertyPage'>
      <div id='PropertyThumb' className='section-b'>
        <div className='container'>
          <h3 className='text-center mt-3 text-camel'>
            {ownerName ? `${t('g_listingsBy')} ${ownerName}` : t('g_ownerListings')}
          </h3>

          {nothingListed && <p className='text-center mt-2'>{t('g_noListings')}</p>}

          {groups.length > 0 && (
            <div className='mt-2'>
              <h4 className='pb-1'>{t('g_propertyComplexes')}</h4>
              <div className='property-thumb'>
                <ul>
                  {populateGroups()}
                </ul>
                <div className='text-center p-1'>
                  {groupMaxPages > 1 && <PageIndex setPage={setGroupPage} setPerPage={setGroupPerPage} page={groupPage} perPage={groupPerPage} maxPages={groupMaxPages} />}
                </div>
              </div>
            </div>
          )}

          {properties.length > 0 && (
            <div className='mt-2'>
              <h4 className='pb-1'>{t('g_rentalProperties')}</h4>
              <div className='property-thumb'>
                <ul>
                  {populateProperties()}
                </ul>
                <div className='text-center p-1'>
                  {maxPages > 1 && <PageIndex setPage={setPage} setPerPage={setPerPage} page={page} perPage={perPage} maxPages={maxPages} />}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PropertyByOwner.propTypes = {
  user: propTypes.instanceOf(Object),
  handlePropertyRequests: propTypes.func.isRequired,
  handlePropertyRemove: propTypes.func.isRequired,
  handlePropertySelect: propTypes.func.isRequired,
  handleLoader: propTypes.func.isRequired,
  handleModal: propTypes.func.isRequired,
  setRedirect: propTypes.func,
};

export default PropertyByOwner;
